import { safeStringify } from '@jira/platform__error-reporting/src/safeStringify.tsx';
import { prependSuperBatchDepsOnce } from '../prependSuperBatchDepsOnce.tsx';
import { loadBridgeWeak } from './loadBridgeWeak.tsx';
import { type BatchDetailsResponseType, safeErrorWithoutCustomerData } from './shared.tsx';

export type LoadWrmResourcesArg = {
	wrmKeys: string[];
	shouldLogError: boolean;
	batchUrl?: BatchDetailsResponseType;
};
/**
 * Initiates the asynchronous loading of Web Resource Manager (WRM) resources using specified keys. It supports
 * both immediate and prefetch loading mechanisms and includes robust error handling, with optional error logging,
 * to ensure a smooth resource loading process. The function is designed to enhance the loading efficiency and
 * reliability of WRM resources, catering to varied loading requirements while safeguarding user data during error logging.
 */
export const loadWrmResources = async ({
	wrmKeys,
	shouldLogError,
	...options
}: LoadWrmResourcesArg): Promise<void> => {
	const isPrefetch = 'batchUrl' in options;
	let newWrmKeys = wrmKeys;
	try {
		const name = isPrefetch ? 'wrm/require-v2' : 'wrm/require';
		const wrmRequireModule = await loadBridgeWeak({
			name,
		});
		// @ts-expect-error - TS2571 - Object is of type 'unknown'.
		const wrmRequire = isPrefetch ? wrmRequireModule.requireWithPrefetch : wrmRequireModule;
		// @ts-expect-error - TS2322 - Type 'string | string[]' is not assignable to type 'string[]'.
		newWrmKeys = prependSuperBatchDepsOnce(wrmKeys);
		if (isPrefetch) {
			return await new Promise(
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
				(resolve: (result: Promise<never>) => void, reject: (error?: any) => void) =>
					// wrmRequire returns a Deferred pretending to be a promise, this converts it to a normal Promise
					wrmRequire(newWrmKeys, options.batchUrl)?.then?.(resolve, reject) ??
					reject(new Error('wrmRequire returned non-Deferred result')),
			);
		}
		return await new Promise(
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			(resolve: (result: Promise<never>) => void, reject: (error?: any) => void) =>
				// wrmRequire returns a Deferred pretending to be a promise, this converts it to a normal Promise
				wrmRequire(newWrmKeys)?.then?.(resolve, reject) ??
				reject(new Error('wrmRequire returned non-Deferred result')),
		);
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		if (shouldLogError) {
			safeErrorWithoutCustomerData('common.bridge.wrm', 'Unable to load wrm resource', {
				isSpa: __SPA__,
				wrmKeys: JSON.stringify(newWrmKeys),
				error: safeStringify(error),
			});
		}
		throw new Error('Unable to load wrm resource', {
			cause: error,
		});
	}
};
